/**
 * Ingest review UI (scope §5.5 / Phase 8): drop a file or paste a URL, read the
 * extraction with its honesty flags, edit, then commit to 00-Inbox.
 */
import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload, Link as LinkIcon, X, Check, AlertTriangle, FileText } from 'lucide-react';
import type { IngestResult } from '@cortex/shared';
import { Api } from '../../lib/api';
import { useUi } from '../../store/ui';
import { qk } from '../../hooks/useVault';

export function IngestDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useUi((s) => s.toast);
  const openNote = useUi((s) => s.openNote);
  const formats = useQuery({ queryKey: ['ingest-formats'], queryFn: Api.ingest.formats, staleTime: 300_000 });
  const [busy, setBusy] = useState<string | null>(null);
  const [results, setResults] = useState<IngestResult[]>([]);
  const [sel, setSel] = useState(0);
  const [edits, setEdits] = useState<Record<string, { title: string; markdown: string; tags: string }>>({});
  const [url, setUrl] = useState('');
  const [folder, setFolder] = useState<string>(() => {
    try {
      return localStorage.getItem('cortex.ingest.folder') || '00-Inbox';
    } catch {
      return '00-Inbox';
    }
  });
  const folders = useQuery({ queryKey: ['ingest-folders'], queryFn: async () => collectFolders(await Api.vault.tree()), staleTime: 60_000 });
  const file = useRef<HTMLInputElement>(null);
  const cur = results[sel];
  const edit = cur ? edits[cur.id] ?? { title: cur.title, markdown: cur.markdown, tags: '' } : null;

  const upload = async (f: File) => {
    setBusy(`Extracting ${f.name}…`);
    try {
      const r = await Api.ingest.upload(f);
      setResults(r.results);
      setSel(0);
      if (!r.results.length) toast('Nothing could be extracted', 'warn');
    } catch (err) {
      toast((err as Error).message, 'crit');
    } finally {
      setBusy(null);
    }
  };
  const fetchUrl = async () => {
    if (!url.trim()) return;
    setBusy('Fetching…');
    try {
      const r = await Api.ingest.url(url.trim());
      setResults(r.results);
      setSel(0);
    } catch (err) {
      toast((err as Error).message, 'crit');
    } finally {
      setBusy(null);
    }
  };
  const commit = async () => {
    if (!cur || !edit) return;
    setBusy('Saving…');
    try {
      const cleanFolder = folder.replace(/^\/+|\/+$/g, '');
      const slug = edit.title.trim().replace(/[\/:*?"<>|#^[\]]+/g, ' ').trim().replace(/\s+/g, '-').slice(0, 120) || 'untitled';
      const autoTags = cleanFolder.split('/').slice(1).map((seg) => seg.toLowerCase()).filter((seg) => seg && seg !== 'datasheets' && seg !== 'knowledge');
      const r = await Api.ingest.commit({ resultId: cur.id, title: edit.title.trim(), markdown: edit.markdown, type: 'reference', path: `${cleanFolder}/${slug}.md`, tags: [...new Set([...autoTags, ...edit.tags.split(',').map((t) => t.trim()).filter(Boolean)])] });
      try {
        localStorage.setItem('cortex.ingest.folder', cleanFolder);
      } catch {
        /* ignore */
      }
      toast(`Saved ${r.path}`);
      qc.invalidateQueries({ queryKey: qk.tree });
      const rest = results.filter((x) => x.id !== cur.id);
      setResults(rest);
      setSel(0);
      if (!rest.length) {
        onClose();
        openNote(r.noteId);
      }
    } catch (err) {
      toast((err as Error).message, 'crit');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog wide ingest-dialog" onDragOver={(e) => e.preventDefault()} onDrop={(e) => (e.preventDefault(), e.dataTransfer.files[0] && void upload(e.dataTransfer.files[0]))}>
        <div className="dialog-head">
          <Upload size={14} /> Ingest a document
          <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}>PDF, DOCX, XLSX/CSV, images{formats.data?.ocr ? ' (OCR)' : ''}, text{formats.data?.urls ? ', URLs' : ''} → a draft note in the folder you choose</span>
          <span className="spacer" />
          <button className="icon" onClick={onClose}><X size={14} /></button>
        </div>
        <div className="dialog-body ingest-body">
          <div className="ingest-sources">
            <button className="ghost" onClick={() => file.current?.click()} disabled={!!busy}><Upload size={12} /> Choose file (or drop it here)</button>
            <input ref={file} type="file" style={{ display: 'none' }} onChange={(e) => e.target.files?.[0] && void upload(e.target.files[0])} />
            {formats.data?.urls !== false && (
              <div className="url-row">
                <LinkIcon size={12} />
                <input type="text" placeholder="https://vendor.example.com/datasheet" value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void fetchUrl()} />
                <button className="ghost" onClick={() => void fetchUrl()} disabled={!!busy || !url.trim()}>Fetch</button>
              </div>
            )}
            {busy && <span className="chip teal">{busy}</span>}
          </div>
          {results.length > 1 && (
            <div className="ingest-tabs">
              {results.map((r, i) => (
                <button key={r.id} className={i === sel ? 'on' : ''} onClick={() => setSel(i)}><FileText size={11} /> {r.meta.sheet ?? r.title}</button>
              ))}
            </div>
          )}
          {cur && edit ? (
            <div className="ingest-review">
              <div className="ingest-meta">
                <span className={`chip ${cur.meta.ingest_confidence >= 0.9 ? 'ok' : cur.meta.ingest_confidence >= 0.6 ? 'warn' : 'crit'}`}>confidence {Math.round(cur.meta.ingest_confidence * 100)}%</span>
                <span className="chip">{cur.meta.kind}</span>
                {cur.meta.ocr && <span className="chip warn"><AlertTriangle size={10} /> OCR — check every number</span>}
                {cur.meta.pages ? <span className="chip">{cur.meta.pages} pages</span> : null}
                <span className="chip">{cur.chars.toLocaleString()} chars</span>
                <span className="faint mono" style={{ fontSize: 11 }}>{cur.meta.source}</span>
              </div>
              {cur.meta.warnings?.map((w, i) => <div key={i} className="pane-banner"><AlertTriangle size={13} /> {w}</div>)}
              <div className="grid2">
                <label>Title<input type="text" value={edit.title} onChange={(e) => setEdits({ ...edits, [cur.id]: { ...edit, title: e.target.value } })} /></label>
                <label>Tags (comma-separated; the folder's vendor is added automatically)<input type="text" value={edit.tags} onChange={(e) => setEdits({ ...edits, [cur.id]: { ...edit, tags: e.target.value } })} placeholder="datasheet, es1" /></label>
                <label>
                  Save to folder
                  <input type="text" list="ingest-folders" value={folder} onChange={(e) => setFolder(e.target.value)} placeholder="50-Reference/Rajant/Datasheets" />
                  <datalist id="ingest-folders">{(folders.data ?? []).map((f) => <option key={f} value={f} />)}</datalist>
                </label>
              </div>
              <label style={{ flex: 1, minHeight: 0 }}>
                Extracted markdown (edit before saving)
                <textarea className="ingest-editor" value={edit.markdown} onChange={(e) => setEdits({ ...edits, [cur.id]: { ...edit, markdown: e.target.value } })} spellCheck={false} />
              </label>
            </div>
          ) : (
            <div className="agent-empty"><FileText size={18} /><div>Drop a datasheet, a spreadsheet or a scanned page. You review the extraction before anything is written; the note carries its source, retrieval time and a confidence figure, and the agent cites OCR'd extracts as such.</div></div>
          )}
        </div>
        <div className="dialog-foot">
          <button className="ghost" onClick={onClose}>Close</button>
          <button className="primary" onClick={() => void commit()} disabled={!cur || !edit?.title.trim() || !!busy}><Check size={12} /> Save to {folder.replace(/^\/+|\/+$/g, '') || '00-Inbox'}{results.length > 1 ? ` (${sel + 1}/${results.length})` : ''}</button>
        </div>
      </div>
    </div>
  );
}

/** Every directory in the vault tree, depth-first, as "10-Sites/Sub" strings. */
function collectFolders(tree: Array<{ path: string; kind?: string; type?: string; children?: unknown[] }>): string[] {
  const out: string[] = [];
  const walk = (nodes: Array<{ path: string; kind?: string; type?: string; children?: unknown[] }>) => {
    for (const n of nodes) {
      const isDir = n.kind === 'dir' || n.type === 'dir' || Array.isArray(n.children);
      if (!isDir || n.path.startsWith('.') || n.path.startsWith('90-')) continue;
      out.push(n.path);
      if (Array.isArray(n.children)) walk(n.children as typeof nodes);
    }
  };
  walk(tree);
  return out.sort();
}
